import * as THREE from 'three'

/**
 * Pre-baked 3D noise stored as a 2D tiled texture.
 * 64×64×64 volume → 512×512 image (8×8 grid of 64×64 slices).
 * Sampled in shaders with trilinear interpolation across slices.
 */

const SLICE_SIZE = 64
const GRID = 8 // 8×8 slices
const TEX_SIZE = SLICE_SIZE * GRID // 512

// Simple hash for baking
function hash3(x: number, y: number, z: number): number {
  let px = Math.abs(x * 0.1031 % 1)
  let py = Math.abs(y * 0.1030 % 1)
  let pz = Math.abs(z * 0.0973 % 1)
  const dot = px * (py + 33.33) + py * (pz + 33.33) + pz * (px + 33.33)
  px += dot; py += dot; pz += dot
  return Math.abs(((px + py) * pz) % 1)
}

function valueNoise3D(x: number, y: number, z: number): number {
  const ix = Math.floor(x), iy = Math.floor(y), iz = Math.floor(z)
  let fx = x - ix, fy = y - iy, fz = z - iz
  fx = fx * fx * (3 - 2 * fx)
  fy = fy * fy * (3 - 2 * fy)
  fz = fz * fz * (3 - 2 * fz)

  const a = hash3(ix, iy, iz)
  const b = hash3(ix + 1, iy, iz)
  const c = hash3(ix, iy + 1, iz)
  const d = hash3(ix + 1, iy + 1, iz)
  const e = hash3(ix, iy, iz + 1)
  const g = hash3(ix + 1, iy, iz + 1)
  const h = hash3(ix, iy + 1, iz + 1)
  const k = hash3(ix + 1, iy + 1, iz + 1)

  const ab = a + (b - a) * fx
  const cd = c + (d - c) * fx
  const eg = e + (g - e) * fx
  const hk = h + (k - h) * fx

  const abcd = ab + (cd - ab) * fy
  const eghk = eg + (hk - eg) * fy

  return abcd + (eghk - abcd) * fz
}

function fbm3D(x: number, y: number, z: number): number {
  let v = 0
  v += 0.5 * valueNoise3D(x, y, z)
  v += 0.25 * valueNoise3D(x * 2.02, y * 2.02, z * 2.02)
  v += 0.125 * valueNoise3D(x * 4.03, y * 4.03, z * 4.03)
  return v // range ~0..0.875
}

let _cachedTexture: THREE.DataTexture | null = null

/** Get (or create) the shared baked noise texture */
export function getDemonNoiseTexture(): THREE.DataTexture {
  if (_cachedTexture) return _cachedTexture

  const data = new Uint8Array(TEX_SIZE * TEX_SIZE * 4)

  for (let sz = 0; sz < SLICE_SIZE; sz++) {
    const sliceRow = Math.floor(sz / GRID)
    const sliceCol = sz % GRID
    const baseX = sliceCol * SLICE_SIZE
    const baseY = sliceRow * SLICE_SIZE

    for (let sy = 0; sy < SLICE_SIZE; sy++) {
      for (let sx = 0; sx < SLICE_SIZE; sx++) {
        // Normalized coords 0..1 mapped to noise space
        const nx = sx / SLICE_SIZE * 4
        const ny = sy / SLICE_SIZE * 4
        const nz = sz / SLICE_SIZE * 4

        const n1 = fbm3D(nx, ny, nz)
        const n2 = fbm3D(nx + 7.3, ny + 3.1, nz + 1.7) // different offset for 2nd channel
        const n3 = fbm3D(nx + 13.7, ny + 9.2, nz + 5.3)

        const px = baseX + sx
        const py = baseY + sy
        const idx = (py * TEX_SIZE + px) * 4
        data[idx] = (n1 * 255) | 0
        data[idx + 1] = (n2 * 255) | 0
        data[idx + 2] = (n3 * 255) | 0
        data[idx + 3] = 255
      }
    }
  }

  _cachedTexture = new THREE.DataTexture(data, TEX_SIZE, TEX_SIZE, THREE.RGBAFormat)
  _cachedTexture.wrapS = THREE.RepeatWrapping
  _cachedTexture.wrapT = THREE.RepeatWrapping
  _cachedTexture.magFilter = THREE.LinearFilter
  _cachedTexture.minFilter = THREE.LinearFilter
  _cachedTexture.needsUpdate = true

  console.log('[Demons] Baked 64³ noise texture (512×512)')
  return _cachedTexture
}

/** GLSL sampling function for the tiled 3D noise texture */
export const NOISE_TEX_SAMPLE_GLSL = /* glsl */ `
  uniform sampler2D uNoiseTex;

  // Sample baked 3D noise from tiled 2D texture
  // p should be in 0..1 range (wraps automatically)
  vec3 sampleNoise3D(vec3 p) {
    p = fract(p); // wrap to 0..1
    float sliceF = p.z * 64.0;
    float slice0 = floor(sliceF);
    float slice1 = slice0 + 1.0;
    float fz = sliceF - slice0;

    // Slice grid position (8×8)
    vec2 uv0 = vec2(
      (mod(slice0, 8.0) * 64.0 + p.x * 63.0 + 0.5) / 512.0,
      (floor(slice0 / 8.0) * 64.0 + p.y * 63.0 + 0.5) / 512.0
    );
    vec2 uv1 = vec2(
      (mod(slice1, 8.0) * 64.0 + p.x * 63.0 + 0.5) / 512.0,
      (floor(slice1 / 8.0) * 64.0 + p.y * 63.0 + 0.5) / 512.0
    );

    vec3 s0 = texture2D(uNoiseTex, uv0).rgb;
    vec3 s1 = texture2D(uNoiseTex, uv1).rgb;
    return mix(s0, s1, fz);
  }
`
